/**
 * admin-reveal-release.js — 後台進場動畫完成後「釋放」transform 鎖
 *
 * 背景（bug 根因）：
 *   後台 [data-reveal] 走 admin.css 的「載入即播」CSS 動畫
 *   `animation: adminReveal … both`（fill-mode:both）。動畫播完後，keyframe 終態
 *   `to { transform: translateY(0) }` 因 fill:both 被永久保留，且 animation 產生的
 *   計算值在 cascade 後套用，特異性概念無法覆蓋它 —— 於是 :hover 的
 *   `transform: translateY(-1px)`（可點 stat 卡）被進場動畫的終態 transform 壓死，
 *   hover 上移在 production 失效。
 *
 * 修法（首選法的安全變體：進場完成後釋放，非「回 CSS 預設」）：
 *   監聽每個 [data-reveal] 的 adminReveal animationend，加 `.reveal-done` class。
 *   `.reveal-done`（admin.css A.0 區塊）卸掉 `animation: none` + 顯式保 `opacity: 1`，
 *   不設 transform —— animation 一卸除，transform 即回到 admin scope base 的
 *   `transform: none`（(0,2,0)），:hover 的 translateY(-1px)（(0,2,1)）因而生效。
 *   opacity 顯式保 1，避免卸 animation 後掉回 base 的 opacity:0（閃爍 / 消失）。
 *
 * 為何安全：
 *   - PRM（prefers-reduced-motion）下 CSS 已把 [data-reveal] 的 animation 設為 none，
 *     無 animation 即無 animationend 事件 → 本檔的 listener 永不觸發，PRM 分支零影響。
 *   - 保險 timeout：animationend 可能因 tab 切走 / 動畫被中斷而漏觸發；
 *     以「最長進場時長」後強制補加 .reveal-done，確保任何情況都會釋放（不卡進場中態）。
 *   - 純加 class，無 DOM 結構副作用；無 [data-reveal] 即實質 no-op。
 *
 * 載入：由 /src/admin.js 中央 import 鏈 auto-init（DOMContentLoaded / 已就緒則立即跑）；
 *   只在 admin/auth/member 殼內生效，前台頁面（無此殼）即 no-op。
 */

const REVEAL_ANIMATION_NAME = 'adminReveal';
const DONE_CLASS = 'reveal-done';

// 保險：最長進場時長 = 最大 stagger（4 階 × 40ms）+ duration（400ms）+ 緩衝。
// 與 admin.css A.0 的 --admin-stagger(40ms) / --admin-reveal-duration(400ms) 對齊；
// 取保守上限，animationend 漏觸發時由此 timeout 兜底釋放。
const FALLBACK_RELEASE_MS = 1200;

function markRevealed(el) {
  if (el && !el.classList.contains(DONE_CLASS)) {
    el.classList.add(DONE_CLASS);
  }
}

function init() {
  // 只在後台 / auth / 會員殼內運作（與 admin.css A.0 的 scope 對齊）。
  const scope = document.querySelector('.page-admin, .page-auth, .page-member');
  if (!scope) return; // 前台頁面（無此殼）→ no-op

  const reveals = scope.querySelectorAll('[data-reveal]');
  if (!reveals.length) return; // 無進場元素 → no-op

  // 進場完成（adminReveal 結束）→ 釋放 transform 鎖。
  // 事件比對 animationName，避免被其他無關 animation（如 toast countdown）誤觸。
  scope.addEventListener('animationend', (e) => {
    if (e.animationName !== REVEAL_ANIMATION_NAME) return;
    if (e.target.hasAttribute('data-reveal')) {
      markRevealed(e.target);
    }
  });

  // 保險兜底：animationend 漏觸發（背景分頁 / 動畫中斷）時，逾時強制全部釋放。
  window.setTimeout(() => {
    reveals.forEach(markRevealed);
  }, FALLBACK_RELEASE_MS);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
